// SPDX-License-Identifier: AGPL-3.0-or-later

import {useSettingsCurrentTab} from '@app/features/app/components/dialogs/shared/SettingsCurrentTabContext';
import {SettingsHeadingLinkButton} from '@app/features/app/components/dialogs/shared/SettingsHeadingLinkButton';
import sectionStyles from '@app/features/app/components/dialogs/shared/SettingsSection.module.css';
import styles from '@app/features/app/components/dialogs/shared/SettingsTabLayout.module.css';
import type {UserSettingsTabType} from '@app/features/user/components/settings_utils/SettingsSectionRegistry';
import {
	buildUserSettingsDeepLink,
	getUserSettingsSectionDeepLinkTarget,
} from '@app/features/user/components/settings_utils/UserSettingsDeepLinks';
import {clsx} from 'clsx';
import type React from 'react';

interface SettingsTabContainerProps {
	children: React.ReactNode;
	className?: string;
}

export const SettingsTabContainer: React.FC<SettingsTabContainerProps> = ({children, className}) => {
	return (
		<div
			className={clsx(styles.container, className)}
			data-flx="app.settings-tab-layout.settings-tab-container.container"
		>
			{children}
		</div>
	);
};

interface SettingsTabHeaderProps {
	title: React.ReactNode;
	description?: React.ReactNode;
	className?: string;
}

export const SettingsTabHeader: React.FC<SettingsTabHeaderProps> = ({title, description, className}) => {
	return (
		<div className={clsx(styles.header, className)} data-flx="app.settings-tab-layout.settings-tab-header.header">
			<h2 className={styles.title} data-flx="app.settings-tab-layout.settings-tab-header.title">
				{title}
			</h2>
			{description && (
				<p className={styles.description} data-flx="app.settings-tab-layout.settings-tab-header.description">
					{description}
				</p>
			)}
		</div>
	);
};

interface SettingsTabContentProps {
	children: React.ReactNode;
	className?: string;
}

export const SettingsTabContent: React.FC<SettingsTabContentProps> = ({children, className}) => {
	return (
		<div className={clsx(styles.content, className)} data-flx="app.settings-tab-layout.settings-tab-content.content">
			{children}
		</div>
	);
};

interface SettingsTabSectionProps {
	id?: string;
	tabType?: UserSettingsTabType;
	title?: React.ReactNode;
	description?: React.ReactNode;
	children: React.ReactNode;
	className?: string;
}

export const SettingsTabSection: React.FC<SettingsTabSectionProps> = ({
	id,
	tabType,
	title,
	description,
	children,
	className,
}) => {
	const currentTab = useSettingsCurrentTab();
	const resolvedTabType = tabType ?? currentTab ?? undefined;
	const linkTarget = id ? getUserSettingsSectionDeepLinkTarget(id, resolvedTabType) : null;
	const linkHref = linkTarget ? buildUserSettingsDeepLink(linkTarget.tab, linkTarget.section) : null;
	return (
		<div
			id={id}
			className={clsx(styles.subsection, className)}
			data-flx="app.settings-tab-layout.settings-tab-section.subsection"
		>
			{(title || description) && (
				<div className={sectionStyles.subsectionHeader} data-flx="app.settings-tab-layout.settings-tab-section.div">
					{title && (
						<div
							className={sectionStyles.sectionTitleRow}
							data-flx="app.settings-tab-layout.settings-tab-section.title-row"
						>
							<h4 className={sectionStyles.subsectionTitle} data-flx="app.settings-tab-layout.settings-tab-section.h4">
								{title}
							</h4>
							{linkHref ? (
								<SettingsHeadingLinkButton
									href={linkHref}
									data-flx="app.settings-tab-layout.settings-tab-section.heading-link-button"
								/>
							) : null}
						</div>
					)}
					{description && (
						<p
							className={sectionStyles.subsectionDescription}
							data-flx="app.settings-tab-layout.settings-tab-section.p"
						>
							{description}
						</p>
					)}
				</div>
			)}
			<div className={sectionStyles.subsectionContent} data-flx="app.settings-tab-layout.settings-tab-section.div--2">
				{children}
			</div>
		</div>
	);
};
